import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../utils/AuthContext';
import { createServiceRequest, Service } from '../../utils/supabaseExtended';
import Button from './Button';

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
  max-width: 600px;
  margin: 0 auto;
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    border-color: #0d9488;
    outline: none;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  background: white;

  &:focus {
    border-color: #0d9488;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  resize: vertical;
  min-height: 100px;

  &:focus {
    border-color: #0d9488;
    outline: none;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: normal;
  cursor: pointer;

  input[type="checkbox"] {
    width: auto;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  color: #155724;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

interface ServiceRequestFormProps {
  service: Service;
  provider?: any;
  onSuccess?: () => void;
}

const ServiceRequestForm: React.FC<ServiceRequestFormProps> = ({ service, provider, onSuccess }) => {
  const { user } = useAuth();
  
  // Form state
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [requestedDate, setRequestedDate] = useState('');
  const [requestedTime, setRequestedTime] = useState('');
  const [preferredTimeSlots, setPreferredTimeSlots] = useState<string[]>([]);
  const [participantsCount, setParticipantsCount] = useState(1);
  const [message, setMessage] = useState('');

  // Status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setClientEmail(user.email || '');
      // Try to get name from email if available
      const nameFromEmail = user.email?.split('@')[0].replace(/[._]/g, ' ') || '';
      setClientName(nameFromEmail);
    }
  }, [user]);

  const handleTimeSlotChange = (slot: string) => {
    setPreferredTimeSlots(prev => 
      prev.includes(slot) 
        ? prev.filter(s => s !== slot)
        : [...prev, slot]
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!user) {
      setError('Devi essere autenticato per richiedere un servizio');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await createServiceRequest({
        serviceId: service.id,
        clientName,
        clientEmail,
        clientPhone,
        requestedDate: requestedDate || undefined,
        requestedTime: requestedTime || undefined,
        preferredTimeSlots: preferredTimeSlots.length > 0 ? preferredTimeSlots : undefined,
        participantsCount,
        message: message || undefined
      });

      if (result.success) {
        setSuccess('Richiesta inviata con successo! Ti contatteremo presto per confermare i dettagli.');
        
        // Reset form
        setRequestedDate('');
        setRequestedTime('');
        setPreferredTimeSlots([]);
        setParticipantsCount(1);
        setMessage('');
        
        onSuccess?.();
      } else {
        setError(result.error || 'Errore durante l\'invio della richiesta');
      }
    } catch (err: any) {
      setError(err.message || 'Errore imprevisto');
    } finally {
      setLoading(false);
    }
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  return (
    <FormContainer>
      <h3 style={{ marginBottom: '1.5rem', color: '#0d9488' }}>
        Richiedi: {service.title_it}
      </h3>
      
      {service.description_it && (
        <p style={{ marginBottom: '2rem', color: '#666', lineHeight: '1.6' }}>
          {service.description_it}
        </p>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <form onSubmit={handleSubmit}>
        <FormSection>
          <Label>Nome Completo *</Label>
          <Input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Email *</Label>
          <Input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Telefono</Label>
          <Input
            type="tel"
            value={clientPhone}
            onChange={(e) => setClientPhone(e.target.value)}
            placeholder="+39 123 456 7890"
          />
        </FormSection>

        <FormSection>
          <Label>Data Preferita</Label>
          <Input
            type="date"
            value={requestedDate}
            onChange={(e) => setRequestedDate(e.target.value)}
            min={today}
          />
        </FormSection>

        <FormSection>
          <Label>Orario Preferito</Label>
          <Input
            type="time"
            value={requestedTime}
            onChange={(e) => setRequestedTime(e.target.value)}
          />
        </FormSection>

        <FormSection>
          <Label>Fasce Orarie Preferite</Label>
          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={preferredTimeSlots.includes('morning')}
                onChange={() => handleTimeSlotChange('morning')}
              />
              Mattina (9:00-12:00)
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={preferredTimeSlots.includes('afternoon')}
                onChange={() => handleTimeSlotChange('afternoon')}
              />
              Pomeriggio (14:00-17:00)
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={preferredTimeSlots.includes('evening')}
                onChange={() => handleTimeSlotChange('evening')}
              />
              Sera (18:00-21:00)
            </CheckboxLabel>
          </CheckboxGroup>
        </FormSection>

        <FormSection>
          <Label>Numero Partecipanti *</Label>
          <Select
            value={participantsCount}
            onChange={(e) => setParticipantsCount(parseInt(e.target.value))}
            required
          >
            {Array.from({ length: service.max_participants }, (_, i) => i + 1).map(num => (
              <option key={num} value={num}>
                {num} {num === 1 ? 'persona' : 'persone'}
              </option>
            ))}
          </Select>
        </FormSection>

        <FormSection>
          <Label>Messaggio Aggiuntivo</Label>
          <TextArea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Descrivi eventuali esigenze particolari, domande o richieste specifiche..."
          />
        </FormSection>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <Button
            type="submit"
            disabled={loading || !user}
            style={{
              flex: 1,
              backgroundColor: loading ? '#6b7280' : '#0d9488',
              padding: '1rem',
              fontSize: '1.1rem'
            }}
          >
            {loading ? 'Invio in corso...' : 'Invia Richiesta'}
          </Button>
        </div>
      </form>

      <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
        <p>* Campi obbligatori</p>
        <p>Ti contatteremo entro 24 ore per confermare disponibilità e dettagli.</p>
      </div>
    </FormContainer>
  );
};

export default ServiceRequestForm;